import * as React from 'react'
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs"
import styled, { css } from 'styled-components'
import Metadata from '../components/Metadata/Metadata'
import Avatar from '../components/Avatar/Avatar'
import Card from '../components/Card/Card'
import ItemContainer from '../components/Item/ItemContainer'
import Reactions from '../components/Reactions/Reactions'
import CardTitle from '../components/Card/CardTitle'
import HelpCenterController from '../controllers/HelpCenterController'
import { useTranslation } from 'react-i18next'
import { HelpCenterArticle, HelpCenterMessengerEventType } from '../../types'
import { useSelector } from 'react-redux'
import { AppState } from '../store'
import { useParams } from 'react-router-dom'
import MobilityHelper from '../../helpers/MobilityHelper'
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView'
import { articleLink, collectionLink, overviewLink } from '../helpers/NavigationHelper'
import moment from '../../utilities/Moment'
import TableOfContent from '../components/TableOfContent/TableOfContent'
import ArticleContent from '../components/Article/ArticleContent'
import { Helmet } from 'react-helmet'
import ItemTitle from '../components/Item/ItemTitle'
import ItemDescription from '../components/Item/ItemDescription'
import ItemWrapper from '../components/Item/ItemWrapper'
import HelpCenterHelper from '../../helpers/HelpCenterHelper'
import ArticleSkeleton from '../components/Skeleton/ArticleSkeleton'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;

  ${ArticleContent} {
    padding-top: 20px;
    margin-top: 20px;
  }
`

const Overview = styled.article`
  display: flex;
  flex-direction: column;

  > {
    h1 {
      color: #1a1a1a;
      line-height: 40px;
      font-weight: 600;
      font-size: 32px;
      margin-bottom: 4px;
    }

    p {
      font-size: 16px;
      margin: 0;
      font-weight: 300;
      color: #737373;
    }
  }
`

const MetadataContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 14px;
`

const ReactionsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 32px;
  background: rgb(242, 242, 242);
  padding: 12px;
  border-radius: 8px;
  min-height: 120px;
`



interface IProps { }
interface IState {
  article: HelpCenterArticle
  relatedArticles: HelpCenterArticle[]
  didInitialLoad: boolean
}

const Article = (props: IProps) => {
  const { currentLocale, helpCenter } = useSelector((state: AppState) => state.helpCenter)
  const embedded = HelpCenterHelper.isEmbedded()
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { id } = useParams<{ id: string }>()

  const { t } = useTranslation()
  const [state, setState] = React.useState<IState>({
    article: null,
    relatedArticles: [],
    didInitialLoad: false,
  })

  const { article, relatedArticles, didInitialLoad } = state

  React.useEffect(() => {
    if (didInitialLoad) {
      HelpCenterHelper.sendParentMessage({
        type: HelpCenterMessengerEventType.ARTICLE_LOADED,
      })
    }
  }, [didInitialLoad])

  const fetchArticle = async () => {
    try {
      const {
        article,
        related_articles,
      } = await HelpCenterController.getArticle(helpCenter.id, id)

      setState({
        article: article,
        relatedArticles: related_articles,
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  const getBreadcrumbs = () => {
    let breadCrumbs: { label: string, to: string }[] = [
      {
        label: t('Collection::All collections'),
        to: overviewLink(currentLocale)
      },
    ]

    let crumbs: { label: string, to: string }[] = []

    if (article.collection) {
      crumbs = [
        ...crumbs,
        {
          label: MobilityHelper.getTranslation(currentLocale, article.collection.translations, 'title'),
          to: collectionLink(article.collection)
        }
      ]

      const parentCollection = article.collection.article_collection

      if (parentCollection) {
        crumbs = [
          ...crumbs,
          {
            label: MobilityHelper.getTranslation(currentLocale, parentCollection.translations, 'title'),
            to: collectionLink(parentCollection)
          }
        ]

        if (parentCollection.article_collection) {
          const grandParentCollection = parentCollection.article_collection

          crumbs = [
            ...crumbs,
            {
              label: MobilityHelper.getTranslation(currentLocale, grandParentCollection.translations, 'title'),
              to: collectionLink(grandParentCollection)
            }
          ]
        }
      }

      crumbs.reverse()
    }

    breadCrumbs = [
      ...breadCrumbs,
      ...crumbs,
      {
        label: MobilityHelper.getTranslation(currentLocale, article.translations, 'title'),
        to: articleLink(article)
      }
    ]

    return breadCrumbs
  }

  React.useEffect(() => {
    fetchArticle().catch(console.error)
  }, [id])

  if (!didInitialLoad) {
    return <ArticleSkeleton />
  }

  const tablesOfContents = []

  return (
    <>
      <Helmet>
        <title>{MobilityHelper.getTranslation(currentLocale, article.translations, 'title')}</title>
        <meta name="description" content={MobilityHelper.getTranslation(currentLocale, article.translations, 'summary')} />
      </Helmet>

      <Container>
        <Overview>
          {!embedded && <Breadcrumbs
            breadcrumbs={getBreadcrumbs()}
          />}

          <h1>{MobilityHelper.getTranslation(currentLocale, article.translations, 'title')}</h1>
          <p>{MobilityHelper.getTranslation(currentLocale, article.translations, 'summary')}</p>

          <Metadata style={{ marginTop: 20 }}>
            {helpCenter.show_article_authors && <Avatar
              name={article?.author?.name || helpCenter.business_name}
              src={article?.author?.avatar_url}
              width={36}
            />}
            <MetadataContent>
              {helpCenter.show_article_authors && <span>
                {t('Article::Written by {{author}}', { author: article?.author?.name || helpCenter.business_name })}
              </span>}
              <span>{t('Article::Updated {{timeAgo}}', { timeAgo: moment(article.updated_at).fromNow() })}</span>
            </MetadataContent>
          </Metadata>

          <ArticleContent ref={contentRef}>
            {helpCenter.table_of_contents_enabled && <TableOfContent contentRef={contentRef} inline={true} />}

            <FroalaEditorView
              model={MobilityHelper.getTranslation(currentLocale, article.translations, 'content')}
            />
          </ArticleContent>

          {helpCenter.related_articles_enabled && relatedArticles?.length > 0 && <Card>
            <CardTitle>
              <h2>{t('Article::Related articles')}</h2>
            </CardTitle>

            {relatedArticles.map((relatedArticle) => (
              <ItemContainer to={articleLink(relatedArticle)} key={relatedArticle.id}>
                <ItemWrapper>
                  <ItemTitle>{MobilityHelper.getTranslation(currentLocale, relatedArticle.translations, 'title')}</ItemTitle>
                  {helpCenter.collection_descriptions_enabled && <ItemDescription style={{ marginTop: 4 }}>{MobilityHelper.getTranslation(currentLocale, relatedArticle.translations, 'summary')}</ItemDescription>}
                </ItemWrapper>

                <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
              </ItemContainer>
            ))}
          </Card>}

          {false && <ReactionsContainer>
            <Reactions
              title={t('Article::Was this article helpful?')}
              reactions={[
                { emoji: '😞', onClick: () => { } },
                { emoji: '😐', onClick: () => { } },
                { emoji: '😃', onClick: () => { } }
              ]}
            />
          </ReactionsContainer>}
        </Overview>

        {helpCenter.table_of_contents_enabled && <TableOfContent contentRef={contentRef} />}
      </Container >
    </>
  )

}

export default Article